import { BookingDetails } from '../domain/BookingDetails'
import { BookingDetailsDTO } from './DTOs/BookingDetailsDTO'
import { BookingFeedback } from '../domain/BookingFeedback'
import { BookingRequest } from '../domain/BookingRequest'
import { BookingRequestDTO } from './DTOs/BookingRequestDTO'
import { Context } from '../domain/Context'
import { DTOAdapter } from './DTOs/DTOAdapter'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, map, firstValueFrom, timer, concatMap, switchMap, take, filter, throwIfEmpty } from 'rxjs'
import { compare } from 'fast-json-patch'
import { environment } from 'projects/booking/src/environments/environment'
import { v4 as uuidv4 } from 'uuid'
import { BookingDocket } from '../domain/BookingDocket'

export type BookingConfirmationTarget = { setupIntentId: string } | { paymentIntentId: string }

@Injectable({
    providedIn: 'root',
})
export class BookingService {

    private baseURL = environment.apiBaseUrl

    constructor(
        private http: HttpClient,
        private dtoAdapter: DTOAdapter
    ) { }

    getBookingDetails(
        bookingId: string
    ): Observable<BookingDetails> {
        const path = `
/booking/details/${encodeURIComponent(bookingId)}`
        const url = new URL(path, this.baseURL)
        return this.http.get<BookingDetailsDTO>(url.toString())
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDetailsDto(dto))
            )
    }

    getUncompletedBookingRequest(context: Context, bookingRequestId: string): Observable<BookingRequest> {
        const path = `/booking/organisation/${encodeURIComponent(context.organisationId)}/business/${encodeURIComponent(context.businessId)}/venue/${encodeURIComponent(context.venueId)}/booking-request/${encodeURIComponent(bookingRequestId)}`
        return this.http.get<BookingRequestDTO>(new URL(path, this.baseURL).toString()).pipe(
            map(dto => this.dtoAdapter.adaptBookingRequestDto(dto))
        )
    }

    requestBooking(
        context: Context,
        bookingRequest: BookingRequest
    ): Observable<BookingDetails> {
        let path = `
/booking\
/organisation/${encodeURIComponent(context.organisationId)}\
/business/${encodeURIComponent(context.businessId)}\
/venue/${encodeURIComponent(context.venueId)}\
/area/${encodeURIComponent(bookingRequest.areaId)}\
/booking`
        const dto = this.dtoAdapter.adaptBookingRequest(bookingRequest)
        let url = new URL(path, this.baseURL)
        let headers = new HttpHeaders()
        .set('Idempotency-Key', bookingRequest.id)
        if (bookingRequest.utmSource) {
            headers = headers.set('X-UTM-Source', bookingRequest.utmSource)
        }
        if (bookingRequest.utmMedium) {
            headers = headers.set('X-UTM-Medium', bookingRequest.utmMedium)
        }
        if (bookingRequest.utmCampaign) {
            headers = headers.set('X-UTM-Campaign', bookingRequest.utmCampaign)
        }
        return this.http.post<BookingDetailsDTO>(url.toString(), dto, { headers })
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDetailsDto(dto))
            )
    }

    confirmBookingReminder(
        bookingId: string,
        reminderId: string
    ): Observable<BookingDetails> {
        const path = `
/booking/${encodeURIComponent(bookingId)}\
/reminder/${encodeURIComponent(reminderId)}\
/confirm`
        const url = new URL(path, this.baseURL)
        return this.http.put<BookingDetailsDTO>(url.toString(), null)
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDetailsDto(dto))
            )
    }

    confirmBooking(bookingId: string, depositPaymentIntentId: string): Observable<BookingDetails> {
        const path = '/booking' +
            `/${encodeURIComponent(bookingId)}` +
            '/confirm'
        const dto = {
            depositPaymentIntentId: depositPaymentIntentId,
        }
        const headers = {
            'Idempotency-Key': uuidv4(),
        }
        const url = new URL(path, this.baseURL)
        return this.http.post<BookingDetailsDTO>(url.toString(), dto, { headers })
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDetailsDto(dto))
            )
    }

    modifyBooking(
        organisationId: string,
        businessId: string,
        venueId: string,
        bookingDetails: BookingDetails,
        updatedBookingDetails: BookingDetails
    ): Observable<BookingDetails> {
        const path = `
/booking\
/organisation/${encodeURIComponent(organisationId)}\
/business/${encodeURIComponent(businessId)}\
/venue/${encodeURIComponent(venueId)}\
/booking/${encodeURIComponent(bookingDetails.bookingId)}`
        const dto = this.dtoAdapter.adaptBookingDetails(bookingDetails)
        const updatedDto = this.dtoAdapter.adaptBookingDetails(updatedBookingDetails)
        const patch = compare(dto, updatedDto)
        const url = new URL(path, this.baseURL)
        const headers = {
            'Content-Type': 'application/json-patch+json',
        }
        return this.http.patch<BookingDetailsDTO>(url.toString(), patch, { headers })
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDetailsDto(dto))
            )
    }

    updateBookingEmailAddress(
        organisationId: string,
        businessId: string,
        venueId: string,
        bookingId: string,
        emailAddress: string
    ): Observable<BookingDetails> {
        const path = `/booking/organisation/${encodeURIComponent(organisationId)}/business/${encodeURIComponent(businessId)}`
            + `/venue/${encodeURIComponent(venueId)}/booking/${encodeURIComponent(bookingId)}`
        return this.http.patch<BookingDetailsDTO>(new URL(path, this.baseURL).toString(), [
            { op: 'test', path: '/bookingEmailAddress', value: null },
            { op: 'replace', path: '/bookingEmailAddress', value: emailAddress },
        ], { headers: { 'Content-Type': 'application/json-patch+json' } }).pipe(
            map(dto => this.dtoAdapter.adaptBookingDetailsDto(dto))
        )
    }

    cancelBooking(
        bookingId: string,
        cancellationReasonId?: string
    ): Observable<BookingDetails> {
        const path = `
/booking/${encodeURIComponent(bookingId)}\
/cancel`
        const url = new URL(path, this.baseURL)
        if (cancellationReasonId) {
            url.searchParams.set('cancellationReasonId', cancellationReasonId)
        }
        const headers = {
            'Idempotency-Key': uuidv4(),
        }
        return this.http.put<BookingDetailsDTO>(url.toString(), null, { headers })
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDetailsDto(dto))
            )
    }

    async submitFeedback(
        bookingId: string,
        feedback: BookingFeedback
    ): Promise<void> {
        const path = `/booking/${encodeURIComponent(bookingId)}/feedback`
        const url = new URL(path, this.baseURL)
        const dto = this.dtoAdapter.adaptBookingFeedback(feedback)
        await firstValueFrom(this.http.post(url.toString(), dto))
    }

    waitForPaymentConfirmation(
        context: Context,
        target: BookingConfirmationTarget,
        beforePoll?: () => Observable<unknown>
    ): Observable<BookingDetails> {
        const poll = () => this.pollBookingConfirmation(
            context.organisationId, context.businessId, context.venueId,
            'setupIntentId' in target ? target.setupIntentId : undefined,
            'paymentIntentId' in target ? target.paymentIntentId : undefined
        )
        return timer(0, 3000).pipe(
            concatMap(() => beforePoll
                ? beforePoll().pipe(switchMap(() => poll()))
                : poll()),
            take(40),
            filter((details): details is BookingDetails => details !== null),
            take(1),
            throwIfEmpty(() => new Error('Booking confirmation timed out'))
        )
    }

    pollBookingConfirmation(
        organisationId: string,
        businessId: string,
        venueId: string,
        setupIntentId?: string,
        paymentIntentId?: string,
        bookingRequestId?: string
    ): Observable<BookingDetails | null> {
        const path = `/booking/organisation/${encodeURIComponent(organisationId)}/business/${encodeURIComponent(businessId)}/venue/${encodeURIComponent(venueId)}/booking`
        const url = new URL(path, this.baseURL)
        if (setupIntentId) {
            url.searchParams.set('setupIntentId', setupIntentId)
        }
        if (paymentIntentId) {
            url.searchParams.set('paymentIntentId', paymentIntentId)
        }
        if (bookingRequestId) {
            url.searchParams.set('bookingRequestId', bookingRequestId)
        }
        return this.http.get<BookingDetailsDTO>(url.toString(), {
            observe: 'response'
        }).pipe(
            map(response => {
                if (response.status === 202) {
                    // Still processing, return null to indicate waiting
                    return null
                } else if (response.status === 200 && response.body) {
                    // Booking confirmed, return the booking details
                    return this.dtoAdapter.adaptBookingDetailsDto(response.body)
                } else {
                    throw new Error('Unexpected response status: ' + response.status)
                }
            })
        )
    }

    updateBookingDocket(
        bookingId: string,
        docket: BookingDocket
    ): Observable<BookingDetails> {
        const path = `/booking/${encodeURIComponent(bookingId)}/docket`
        const url = new URL(path, this.baseURL)
        const dto = this.dtoAdapter.adaptBookingDocket(docket)
        return this.http.put<BookingDetailsDTO>(url.toString(), dto)
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDetailsDto(dto))
            )
    }
}
